import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './Home.css';
import { IMAGE_BASE_URL } from '../constant';

const Home = () => {
  const { currentLanguage } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  const heroSlides = [
    {
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920&q=80&fit=crop',
      tag:   'AN ISO 9001:2015 & ISO 22000:2018 CERTIFIED',
      title: 'Premium Seeds for\na Better Future',
      sub:   'High-quality seeds for better yield and sustainable farming across every season.',
    },
    {
      image: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=1920&q=80&fit=crop',
      tag:   'TRUSTED BY 10,000+ FARMERS',
      title: 'Grow More,\nEarn More',
      sub:   'Scientifically developed hybrid varieties with superior disease resistance and high productivity.',
    },
    {
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=1920&q=80&fit=crop',
      tag:   'INNOVATION IN AGRICULTURE',
      title: 'Seeds That Power\nEvery Harvest',
      sub:   'From soil to success — our seeds are engineered for maximum germination and crop performance.',
    },
    {
      image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=1920&q=80&fit=crop',
      tag:   'SUSTAINABLE FARMING',
      title: 'Nature\'s Best,\nScientifically Proven',
      sub:   'Eco-friendly seed solutions that respect the land while delivering outstanding results.',
    },
    {
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1920&q=80&fit=crop',
      tag:   'GANGASEED® — THE FARMER\'S NEED',
      title: 'Your Partner in\nAgricultural Excellence',
      sub:   'Decades of expertise, thousands of happy farmers, and seeds you can always count on.',
    },
  ];

  // Auto-slide with crossfade
  useEffect(() => {
    const timer = setInterval(() => {
      if (animating) return;
      setAnimating(true);
      setPrevSlide(currentSlide);
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
      setTimeout(() => { setPrevSlide(null); setAnimating(false); }, 1000);
    }, 5000);
    return () => clearInterval(timer);
  }, [currentSlide, animating, heroSlides.length]);

  const goToSlide = (idx) => {
    if (animating || idx === currentSlide) return;
    setAnimating(true);
    setPrevSlide(currentSlide);
    setCurrentSlide(idx);
    setTimeout(() => { setPrevSlide(null); setAnimating(false); }, 1000);
  };

  // Fetch featured products from API
  useEffect(() => {
    fetch(`/api/products?lang=${currentLanguage}`)
      .then(res => res.json())
      .then(data => {
        setFeaturedProducts(Array.isArray(data) ? data.slice(0, 4) : []);
      })
      .catch(err => console.error('❌ Featured products error:', err));
  }, [currentLanguage]);

  return (
    <div className="home">

      {/* ══════════════════════════════════════════
          ULTRA PREMIUM HERO SECTION
      ══════════════════════════════════════════ */}
      <section className="hero-section">

        {/* Background image layers */}
        <div className="hero-bg-layers">
          {heroSlides.map((slide, i) => (
            <div
              key={i}
              className={`hero-bg-layer
                ${i === currentSlide ? 'hero-bg-active' : ''}
                ${i === prevSlide    ? 'hero-bg-prev'   : ''}
              `}
              style={{ backgroundImage: `url(${slide.image})` }}
            />
          ))}
        </div>

        {/* Gradient overlays */}
        <div className="hero-overlay-dark" />
        <div className="hero-overlay-gradient" />

        {/* Floating decorative orbs */}
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="hero-orb hero-orb-3" />

        {/* Content */}
        <div className="container hero-content-wrap">
          {heroSlides.map((slide, i) => (
            <div
              key={i}
              className={`hero-content-slide ${i === currentSlide ? 'hero-content-active' : ''}`}
            >
              {/* Tag badge */}
              <div className="hero-tag">
                <span className="hero-tag-dot" />
                {slide.tag}
              </div>

              {/* Main heading */}
              <h1 className="hero-heading">
                {slide.title.split('\n').map((line, li) => (
                  <span key={li} className="hero-heading-line">{line}</span>
                ))}
              </h1>

              {/* Subheading */}
              <p className="hero-sub">{slide.sub}</p>

              {/* CTA Buttons */}
              <div className="hero-cta-row">
                <Link to="/products" className="hero-btn-primary">
                  <span className="hero-btn-shine" />
                  <span>View Products</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
                <Link to="/contact" className="hero-btn-secondary">
                  <span>Contact Us</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>
                </Link>
              </div>

              {/* Stats row */}
              <div className="hero-stats-row">
                <div className="hero-stat">
                  <span className="hero-stat-num">25+</span>
                  <span className="hero-stat-lbl">Years Experience</span>
                </div>
                <div className="hero-stat-divider" />
                <div className="hero-stat">
                  <span className="hero-stat-num">10K+</span>
                  <span className="hero-stat-lbl">Happy Farmers</span>
                </div>
                <div className="hero-stat-divider" />
                <div className="hero-stat">
                  <span className="hero-stat-num">98%</span>
                  <span className="hero-stat-lbl">Success Rate</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Slide indicators */}
        <div className="hero-indicators">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              className={`hero-dot ${i === currentSlide ? 'hero-dot-active' : ''}`}
              onClick={() => goToSlide(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Slide counter */}
        <div className="hero-counter">
          <span className="hero-counter-cur">{String(currentSlide + 1).padStart(2, '0')}</span>
          <span className="hero-counter-sep">/</span>
          <span className="hero-counter-tot">{String(heroSlides.length).padStart(2, '0')}</span>
        </div>

        {/* Scroll hint */}
        <div className="hero-scroll-hint">
          <div className="hero-scroll-mouse">
            <div className="hero-scroll-wheel" />
          </div>
          <span>Scroll Down</span>
        </div>

        {/* Bottom thumbnail strip */}
        <div className="hero-thumb-strip">
          {heroSlides.map((slide, i) => (
            <button
              key={i}
              className={`hero-thumb ${i === currentSlide ? 'hero-thumb-active' : ''}`}
              onClick={() => goToSlide(i)}
              style={{ backgroundImage: `url(${slide.image})` }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ── Scrolling Ticker ── */}
      <div className="ticker-wrap">
        <div className="ticker-track">
          {[0, 1].map(i => (
            <span key={i} className="ticker-text">
              &nbsp;&nbsp;&nbsp;🌾&nbsp; GANGASEED® — THE FARMER'S NEED
              &nbsp;&nbsp;&nbsp;❋&nbsp; AN ISO 9001:2015 &amp; ISO 22000:2018 CERTIFIED SEED COMPANY
              &nbsp;&nbsp;&nbsp;🌱&nbsp; Premium Quality Seeds For Every Farming Need
              &nbsp;&nbsp;&nbsp;❋&nbsp; GANGASEED® — THE FARMER'S NEED
              &nbsp;&nbsp;&nbsp;🌾&nbsp;
            </span>
          ))}
        </div>
      </div>
      <section className="about-preview section">
        <div className="container">
          <div className="about-preview-content">
            <div className="about-preview-text glass-card">
              <h2 className="section-title" style={{textAlign: 'left'}}>About Gangaseed</h2>
              <p className="about-preview-description">
                Gangaseed Company is a progressive agricultural company dedicated to delivering 
                high-quality seeds that empower farmers and enhance crop productivity. With a strong focus 
                on innovation, research, and sustainability, we aim to support modern farming practices while 
                preserving the essence of traditional agriculture.
              </p>
              <p className="about-preview-description">
                Our commitment lies in providing reliable, high-performance seeds that ensure better yield, 
                disease resistance, and adaptability to diverse climatic conditions.
              </p>
              <Link to="/about" className="btn-secondary">Learn More About Us</Link>
            </div>
            <div className="about-preview-image">
              <div className="image-card glass-card">
                <div className="preview-icon">🌱</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products — dynamic from DB */}
      <section className="featured-products section" style={{backgroundColor: 'var(--white)'}}>
        <div className="container">
          <h2 className="section-title">Featured Products</h2>
          <p className="section-subtitle">
            Discover our most popular and high-performing seed varieties
          </p>

          {featuredProducts.length > 0 ? (
            <div className="featured-grid">
              {featuredProducts.map(product => (
                <Link
                  key={product.id}
                  to={`/products/${product.slug}`}
                  className="featured-card glass-card"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {product.image_url ? (
                    <img
                      src={`${IMAGE_BASE_URL}${product.image_url}`}
                      alt={product.name}
                      className="featured-product-img"
                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }}
                    />
                  ) : null}
                  <div className="featured-image" style={{ display: product.image_url ? 'none' : 'block' }}>
                    {product.icon || '🌱'}
                  </div>
                  <h3>{product.name || product.slug}</h3>
                  <p>{product.description || 'Premium quality seeds'}</p>
                  <span className="featured-link">View Details →</span>
                </Link>
              ))}
            </div>
          ) : (
            /* Fallback static cards while loading */
            <div className="featured-grid">
              {[
                { icon: '🥬', name: 'Cabbage Seeds', desc: 'Premium variety with tight heads and excellent storage quality', slug: 'cabbage' },
                { icon: '🥕', name: 'Beetroot Seeds', desc: 'High-quality beetroot with vibrant color and sweet taste', slug: 'beetroot' },
                { icon: '🍅', name: 'Tomato Seeds', desc: 'Hybrid variety with disease resistance and high yield', slug: 'tomato' },
                { icon: '🥒', name: 'Cucumber Seeds', desc: 'Crisp texture with excellent productivity and taste', slug: 'cucumber' },
              ].map(p => (
                <Link key={p.slug} to={`/products/${p.slug}`} className="featured-card glass-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="featured-image">{p.icon}</div>
                  <h3>{p.name}</h3>
                  <p>{p.desc}</p>
                  <span className="featured-link">View Details →</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose section">
        <div className="container">
          <h2 className="section-title">Why Choose GANGASEED?</h2>
          <p className="section-subtitle">
            AN ISO 9001:2015 &amp; ISO 22000:2018 Certified Seed Company
          </p>
          <div className="why-choose-grid">
            <div className="why-card glass-card">
              <div className="why-icon">🌾</div>
              <h3>High Yield</h3>
              <p>Scientifically developed to maximize crop yield and productivity</p>
            </div>
            <div className="why-card glass-card">
              <div className="why-icon">🛡️</div>
              <h3>Disease Resistant</h3>
              <p>Enhanced resistance to common diseases and pests</p>
            </div>
            <div className="why-card glass-card">
              <div className="why-icon">🌍</div>
              <h3>Eco-Friendly</h3>
              <p>Sustainable farming with environmentally conscious production</p>
            </div>
            <div className="why-card glass-card">
              <div className="why-icon">✅</div>
              <h3>Quality Certified</h3>
              <p>Tested and certified to meet international standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section section" style={{backgroundColor: 'var(--white)'}}>
        <div className="container">
          <div className="cta-card glass-card">
            <div className="cta-content">
              <h2>Ready to Grow Your Success?</h2>
              <p>Get in touch with us today and discover how our premium seeds can transform your harvest</p>
              <div className="cta-buttons">
                <Link to="/products" className="btn-primary">View All Products</Link>
                <Link to="/contact" className="btn-secondary">Contact Us</Link>
              </div>
            </div>
            <div className="cta-stats">
              <div className="stat-item glass-card">
                <h3 className="stat-number">25+</h3>
                <p className="stat-label">Years Experience</p>
              </div>
              <div className="stat-item glass-card">
                <h3 className="stat-number">10,000+</h3>
                <p className="stat-label">Happy Farmers</p>
              </div>
              <div className="stat-item glass-card">
                <h3 className="stat-number">98%</h3>
                <p className="stat-label">Success Rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
